import React, { memo } from 'react';

import { IGroupDetail } from 'core/hooks';

import {
  OverviewNavTarget,
  OverviewSummaryGrid,
  UpcomingLessonsSection,
  RecentActivitySection,
} from '../parts/overview';

interface IOverviewTabProps {
  /** The group meta loaded by the page shell (slim `GroupDetailQuery`). */
  group: IGroupDetail;
  /**
   * Tab-navigation callback so a summary widget or an upcoming-lesson row can
   * switch the active dashboard tab (Req 2.4). `OverviewNavTarget` is a subset
   * of `TabId`, so the page shell (task 19.1) can pass
   * `useGroupDeepLinkState().setTab` directly — the widget targets
   * (`students | schedule | finances | zoom | pandadoc | materials`) are all
   * valid `TabId`s.
   */
  onNavigate: (target: OverviewNavTarget) => void;
}

/**
 * OverviewTab — the reworked Overview summary landing page (Req 1–2).
 *
 * It composes three independently-loading pieces:
 *   - `OverviewSummaryGrid` — attendance / performance / money health widgets.
 *   - `UpcomingLessonsSection` — next lessons (hidden when the group finished).
 *   - `RecentActivitySection` — always rendered recent-activity feed.
 *
 * The old flat key-value card (course / price / students / lessons /
 * contributions / teachers) is removed entirely so the Overview no longer
 * duplicates the HeaderBar identity fields or the KPI-strip values as a
 * key-value list (Req 2.5).
 *
 * Intentional design decision — NO single `TabStateView` wraps the whole tab.
 * Per the design ("Overview rework"), the Overview composes widgets that each
 * own their own loading / empty / populated states backed by their own data
 * hook (Req 1.3–1.5). A single `TabStateView` only makes sense around content
 * fed by one query/error pair; forcing one here would couple three independent
 * queries to a single loading/error gate and hide a healthy widget behind a
 * sibling's load. So each child handles its own terminal states and OverviewTab
 * is a thin composition that just forwards `group.id`, the finished flag, and
 * the `onNavigate` callback.
 */
export const OverviewTab = memo(({ group, onNavigate }: IOverviewTabProps) => (
  <>
    <OverviewSummaryGrid groupId={group.id} onNavigate={onNavigate} />
    <UpcomingLessonsSection
      groupId={group.id}
      statusFinished={Boolean(group.statusFinished)}
      onNavigate={onNavigate}
    />
    <RecentActivitySection groupId={group.id} />
  </>
));
